import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, Send } from "lucide-react";
import { api, idr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import LoadingState from "../../components/ui/loading-state";
import { formatTanggal } from "../../lib/tanggal";

/* ============================================================================
 * Nota Belum Terposting (batch UANG-KASIR N0, 23 Sep 2026).
 *
 * KENAPA ADA: posting penjualan yang timpang/tanpa akun dulu DILEWATI tanpa
 * jejak di layar mana pun — empat nota 22 Sep hilang dari buku besar begitu
 * saja. Daftar ini membaca `GET /finance/audit/nota-belum-terposting`
 * (disaring & dipotong DI SERVER; menyaring daftar terpotong di layar adalah
 * pencarian yang berbohong) dan menampilkan ALASAN tiap nota.
 *
 * Tombol "Posting ulang" hanya untuk pemegang `finance_audit.update` — kunci
 * yang SAMA dengan `require_permission` endpoint-nya (aturan 3 CLAUDE.md), dan
 * hanya pada baris yang menurut server boleh (`boleh_posting_ulang`).
 * ========================================================================== */

const KELOMPOK = [
  ["belum_terposting", "Belum terposting"],
  ["jurnal_versi_lama", "Jurnal versi lama"],
  ["ditambal", "Ditambal (<2%)"],
  ["impor_historis", "Impor / historis"],
  ["tidak_perlu_jurnal", "Bernilai nol"],
  // INV-SN (22 Sep 2026): nota SUDAH terposting tetapi HPP sebagian barisnya
  // dilewati — uangnya benar, persediaannya tidak berkurang untuk baris itu.
  ["hpp_dilewati", "HPP baris dilewati"],
];

export default function NotaBelumTerposting({ dariAwal, sampaiAwal }) {
  const { can } = useAuth();
  const bolehPostingUlang = can("finance_audit", "update");
  const [filter, setFilter] = useState({
    dari: dariAwal || "", sampai: sampaiAwal || "", cabang: "", kelompok: "belum_terposting",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [proses, setProses] = useState("");

  const load = () => {
    setLoading(true);
    setLoadError(null);
    const params = { kelompok: filter.kelompok, limit: 200 };
    if (filter.dari) params.dari = filter.dari;
    if (filter.sampai) params.sampai = filter.sampai;
    if (filter.cabang.trim()) params.cabang = filter.cabang.trim();
    api.get("/finance/audit/nota-belum-terposting", { params })
      .then(r => setData(r.data))
      .catch(e => { setLoadError(e); toast.error(e?.response?.data?.detail || "Gagal memuat daftar"); })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [filter.kelompok]);

  const postingUlang = async (n) => {
    let body = {};
    if (n.butuh_konfirmasi_bulan) {
      const bulan = String(n.tanggal || "").slice(0, 7);
      const jawab = window.prompt(
        `Nota ini bertanggal bulan ${bulan}. Posting ulang akan MENGUBAH angka bulan itu.\n\n`
        + `Ketik ${bulan} untuk melanjutkan:`, "");
      if (jawab === null) return;
      if (jawab.trim() !== bulan) { toast.error(`Konfirmasi harus persis ${bulan}`); return; }
      body = { konfirmasi_bulan: bulan };
    } else if (!window.confirm(`Posting ulang nota ${n.nama_pelanggan || ""} (${idr(n.total_harga)}) ke buku besar?`)) {
      return;
    }
    setProses(n.id);
    try {
      const { data: r } = await api.post(`/finance/audit/nota-belum-terposting/${n.id}/posting-ulang`, body);
      if (r.ok) toast.success(r.message || "Nota terposting");
      else toast.warning(r.message || "Nota tetap belum bisa diposting");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal posting ulang");
    } finally {
      setProses("");
    }
  };

  const per = data?.per_kelompok || {};
  const items = data?.items || [];

  return (
    <div className="space-y-4" data-testid="nbt-panel">
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-600">Dari
          <input type="date" value={filter.dari} data-testid="nbt-dari"
            onChange={e => setFilter(f => ({ ...f, dari: e.target.value }))}
            className="block h-9 px-2 border border-slate-200 rounded-lg text-sm" />
        </label>
        <label className="text-xs text-slate-600">Sampai
          <input type="date" value={filter.sampai} data-testid="nbt-sampai"
            onChange={e => setFilter(f => ({ ...f, sampai: e.target.value }))}
            className="block h-9 px-2 border border-slate-200 rounded-lg text-sm" />
        </label>
        <label className="text-xs text-slate-600">Cabang
          <input value={filter.cabang} data-testid="nbt-cabang" placeholder="semua"
            onChange={e => setFilter(f => ({ ...f, cabang: e.target.value }))}
            className="block h-9 px-2 border border-slate-200 rounded-lg text-sm w-40" />
        </label>
        <button onClick={load} data-testid="nbt-muat"
          className="h-9 px-3 rounded-lg border border-slate-300 text-sm inline-flex items-center gap-1.5 hover:bg-slate-50">
          <RefreshCw className="w-4 h-4" /> Terapkan
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {KELOMPOK.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(f => ({ ...f, kelompok: k }))}
            data-testid={`nbt-kelompok-${k}`}
            className={`text-xs px-2.5 py-1 rounded-full border ${filter.kelompok === k
              ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
            {l} <span className="opacity-70">({per[k] || 0})</span>
          </button>
        ))}
      </div>
      {filter.kelompok === "hpp_dilewati" && (
        <p className="text-[11px] text-slate-500" data-testid="nbt-catatan-hpp-dilewati">
          Nota di sini sudah masuk buku besar, tetapi HPP beberapa barisnya dilewati (mis. barang ber-IMEI
          dijual tanpa menyebut unit). Persediaan untuk baris itu tidak berkurang. Posting ulang tidak
          mengubahnya — perbaiki lewat edit nota (pilih unit IMEI/SN) atau koreksi Finance.
        </p>
      )}
      {filter.kelompok === "impor_historis" && (
        <p className="text-[11px] text-slate-500">
          Dokumen impor/historis tidak pernah diposting otomatis. Posting ulangnya dimatikan sampai ada
          keputusan owner — satu klik akan menjurnal pendapatan ke bulan lama.
        </p>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <LoadingState loading={loading} error={loadError} onRetry={load} varian="kartu" jumlahKartu={3}
          data-testid="loading-nbt">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-600">Tidak ada nota di kelompok ini.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Tanggal</th>
                    <th className="px-3 py-2 text-left">Pelanggan · Cabang</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-left">Metode · Asal</th>
                    <th className="px-3 py-2 text-left">Alasan</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {items.map(n => (
                    <tr key={n.id} className="border-b border-slate-50 align-top" data-testid={`nbt-baris-${n.id}`}>
                      <td className="px-3 py-2 text-xs font-mono text-slate-600 whitespace-nowrap">
                        {formatTanggal(n.tanggal)}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <p className="text-slate-900">{n.nama_pelanggan || "-"}</p>
                        <p className="text-slate-500">{n.branch || "(tanpa cabang)"}</p>
                      </td>
                      <td className="px-3 py-2 text-xs text-right font-mono">{idr(n.total_harga)}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">
                        <p>{n.metode_pembayaran || "-"}</p>
                        <p className="text-slate-400">{n.asal_label}</p>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700 max-w-md">
                        {n.riwayat === "dibalik_saat_edit" && (
                          <p className="text-rose-700 font-semibold flex items-center gap-1">
                            <AlertTriangle className="w-3.5 h-3.5" /> Pernah terposting, jurnalnya dibalik saat edit
                          </p>
                        )}
                        <p>{n.diagnosa}</p>
                        {!n.boleh_posting_ulang && n.alasan_tidak_boleh && (
                          <p className="text-slate-400 italic mt-0.5">{n.alasan_tidak_boleh}</p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {bolehPostingUlang && n.boleh_posting_ulang && (
                          <button onClick={() => postingUlang(n)} disabled={proses === n.id}
                            data-testid={`nbt-posting-ulang-${n.id}`}
                            className="text-xs px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1 disabled:opacity-50">
                            <Send className="w-3.5 h-3.5" /> {proses === n.id ? "..." : "Posting ulang"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </LoadingState>
      </div>
      {data && data.total > items.length && (
        <p className="text-[11px] text-slate-500">
          Menampilkan {items.length} dari {data.total} nota — persempit rentang tanggal atau cabang.
        </p>
      )}
    </div>
  );
}
